import React from 'react';
import { Alert, Button, Card, Modal, Popconfirm, Space, Table } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { ParserErrorDto } from '@poker-stats/contracts';
import { deleteParserError, loadParserErrors } from './reducer';
import {
  selectParserErrorDeletingId,
  selectParserErrors,
  selectParserErrorsError,
  selectParserErrorsLoading,
  selectParserErrorsPage,
  selectParserErrorsPageSize,
  selectParserErrorsTotal,
} from './selectors';
import './ParserErrorsPage.css';

type ParserErrorsPageProps = {
  embedded?: boolean;
};

const ParserErrorsPage = React.memo(({ embedded = false }: ParserErrorsPageProps) => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const items = useSelector(selectParserErrors);
  const total = useSelector(selectParserErrorsTotal);
  const page = useSelector(selectParserErrorsPage);
  const pageSize = useSelector(selectParserErrorsPageSize);
  const loading = useSelector(selectParserErrorsLoading);
  const deletingId = useSelector(selectParserErrorDeletingId);
  const error = useSelector(selectParserErrorsError);
  const [selectedError, setSelectedError] = React.useState<ParserErrorDto>();

  React.useEffect(() => {
    dispatch(loadParserErrors(undefined));
  }, [dispatch]);

  const handleTableChange = React.useCallback(
    (pagination: TablePaginationConfig) => {
      dispatch(
        loadParserErrors({
          page: pagination.current ?? 1,
          pageSize: pagination.pageSize ?? 20,
        }),
      );
    },
    [dispatch],
  );

  const columns = React.useMemo(
    () => [
      {
        title: t('admin_parser_error_date'),
        dataIndex: 'parsedAt',
        width: 168,
        render: (value: string) => dayjs(value).format('DD.MM.YY HH:mm:ss'),
      },
      {
        title: t('admin_parser_error_room'),
        dataIndex: 'room',
        width: 96,
        render: (value: string) => <span className="v2-admin-room-tag">{value}</span>,
      },
      {
        title: t('admin_parser_error_hand'),
        dataIndex: 'handText',
        render: (value: string, row: ParserErrorDto) => (
          <div className="ParserErrorsPage__HandCell">
            <p className="ParserErrorsPage__Preview">{value}</p>
            <button
              type="button"
              className="v2-admin-text-link"
              onClick={() => setSelectedError(row)}
            >
              {t('admin_parser_error_view')}
            </button>
          </div>
        ),
      },
      {
        title: t('admin_parser_error_actions'),
        key: 'actions',
        width: 96,
        render: (_value: unknown, row: ParserErrorDto) => (
          <Popconfirm
            title={t('admin_parser_error_delete_confirm')}
            okText={t('delete')}
            cancelText={t('cancel')}
            okButtonProps={{ danger: true }}
            onConfirm={() => dispatch(deleteParserError(row.id))}
          >
            <button type="button" className="v2-admin-link-danger" disabled={deletingId === row.id}>
              {deletingId === row.id ? '…' : t('delete')}
            </button>
          </Popconfirm>
        ),
      },
    ],
    [deletingId, dispatch, t],
  );

  const errorAlert = error ? (
    <Alert
      type="error"
      showIcon
      message={error}
      action={
        embedded ? (
          <button
            type="button"
            className="outline-button"
            onClick={() => dispatch(loadParserErrors(undefined))}
          >
            {t('retry')}
          </button>
        ) : (
          <Button size="small" onClick={() => dispatch(loadParserErrors(undefined))}>
            {t('retry')}
          </Button>
        )
      }
    />
  ) : null;

  const table = (
    <Table<ParserErrorDto>
      rowKey="id"
      loading={loading}
      dataSource={items}
      scroll={{ x: 760 }}
      locale={{ emptyText: t('admin_parser_errors_empty') }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: !embedded,
        pageSizeOptions: [20, 50, 100],
      }}
      onChange={handleTableChange}
      columns={columns}
    />
  );

  const modal = (
    <Modal
      open={Boolean(selectedError)}
      width={900}
      className={embedded ? 'v2-admin-modal' : undefined}
      title={t('admin_parser_error_hand_title', { id: selectedError?.id ?? '' })}
      footer={null}
      onCancel={() => setSelectedError(undefined)}
    >
      <pre className="ParserErrorsPage__HandText">{selectedError?.handText}</pre>
    </Modal>
  );

  if (embedded) {
    return (
      <div className="v2-admin-parser-errors">
        {errorAlert ? <div className="v2-admin-alert">{errorAlert}</div> : null}
        <section className="settings-panel v2-admin-table-panel">
          <div className="settings-panel__head v2-admin-table-panel__head">
            <h2>Журнал ошибок</h2>
            <p className="v2-admin-table-panel__meta">
              Всего: <strong>{total}</strong>
            </p>
          </div>
          <div className="table-scroll v2-admin-table-scroll">{table}</div>
        </section>
        {modal}
      </div>
    );
  }

  return (
    <Card className="ParserErrorsPage" title={t('admin_parser_errors')}>
      <Space direction="vertical" size={16} className="ParserErrorsPage__Stack">
        {errorAlert}
        {table}
      </Space>
      {modal}
    </Card>
  );
});

export default ParserErrorsPage;
